import { useState } from "react";
import { toast } from "sonner";
import { Tag, ArrowRightLeft } from "lucide-react";
import { api, errMsg, money, fmtDateTime } from "@/lib/api";
import { inputCls, btnPrimary, btnOutline, btnGhost } from "@/lib/ui";

const STATUS = {
  pending: "Waiting on seller", countered: "Countered — waiting on buyer", accepted: "Accepted",
  declined: "Declined", auto_declined: "Not accepted", replaced: "Replaced by newer offer",
  expired: "Expired", scheduled: "Pickup confirmed", closed: "Closed",
};

export const OfferCard = ({ offer, role, onChange, onSchedule }) => {
  const [counter, setCounter] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const [busy, setBusy] = useState(false);
  const myTurn = (offer.status === "pending" && role === "seller") || (offer.status === "countered" && role === "buyer");
  const act = async (action, amount) => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/respond`, { action, amount });
      toast.success(action === "accept" ? "Offer accepted" : action === "decline" ? "Offer declined" : "Counteroffer sent");
      setShowCounter(false);
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const live = ["pending", "countered", "accepted"].includes(offer.status);
  return (
    <div data-testid={`offer-card-${offer.id}`} className={`rounded-2xl p-4 space-y-3 max-w-sm w-full ${live ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
        <Tag className="h-3.5 w-3.5" /> Offer · <span data-testid="offer-status">{STATUS[offer.status]}</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span data-testid="offer-amount" className="text-3xl font-extrabold font-mono">{money(offer.final_amount ?? offer.amount)}</span>
        {offer.status === "countered" && (
          <span className="text-sm flex items-center gap-1"><ArrowRightLeft className="h-4 w-4" /> Seller counters <b data-testid="offer-counter-amount" className="font-mono text-blue-300">{money(offer.counter_amount)}</b></span>
        )}
      </div>
      {offer.proposed_window && <p className="text-sm opacity-80">Proposed pickup: {offer.proposed_window}</p>}
      {offer.status === "accepted" && (
        <p className="text-xs opacity-80">Not reserved yet. Schedule a pickup before {fmtDateTime(offer.accepted_expires_at)} to keep this price.</p>
      )}
      {offer.status === "auto_declined" && role === "buyer" && <p className="text-xs">The seller can't accept this amount. You can try a different offer.</p>}
      {myTurn && !showCounter && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="offer-accept-btn" disabled={busy} className={btnPrimary + " h-10"} onClick={() => act("accept")}>
            Accept{offer.status === "countered" ? ` ${money(offer.counter_amount)}` : ""}
          </button>
          <button data-testid="offer-counter-btn" disabled={busy} className={btnOutline + " h-10 text-slate-900"} onClick={() => setShowCounter(true)}>Counter</button>
          <button data-testid="offer-decline-btn" disabled={busy} className={btnGhost + " text-slate-300 hover:bg-white/10"} onClick={() => act("decline")}>Decline</button>
        </div>
      )}
      {myTurn && showCounter && (
        <div className="flex gap-2">
          <input data-testid="offer-counter-input" type="number" min="1" placeholder="Amount" value={counter} onChange={(e) => setCounter(e.target.value)} className={inputCls + " h-10 text-slate-900"} />
          <button data-testid="offer-counter-submit" disabled={busy || !counter} className={btnPrimary + " h-10"} onClick={() => act("counter", Number(counter))}>Send</button>
          <button className={btnGhost + " text-slate-300"} onClick={() => setShowCounter(false)}>Cancel</button>
        </div>
      )}
      {offer.status === "accepted" && role === "buyer" && onSchedule && (
        <button data-testid="offer-schedule-btn" className={btnPrimary + " h-10 w-full"} onClick={() => onSchedule(offer)}>Schedule pickup</button>
      )}
    </div>
  );
};
